import { Injectable } from '@angular/core';
import { PublicClientApplication, BrowserCacheLocation, InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '../../environments/environment';

interface SwaClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthTokenService {
  private token: string = '';

  private readonly msalInstance = new PublicClientApplication({
    auth: {
      clientId: environment.aadClientId,
      authority: `https://login.microsoftonline.com/${environment.aadTenantId}`,
      redirectUri: window.location.origin + '/'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage
    }
  });

  async initialize(): Promise<void> {
    await this.msalInstance.initialize();

    const scopes = [`api://${environment.aadClientId}/user_impersonation`];

    // Process the return leg of any MSAL redirect.
    // Track whether we came back from a redirect so we never redirect a second time
    // in the same session — that is the source of the infinite loop.
    let justReturnedFromRedirect = false;
    try {
      const redirectResult = await this.msalInstance.handleRedirectPromise();
      if (redirectResult !== null) {
        justReturnedFromRedirect = true;
        if (redirectResult.accessToken) {
          this.setToken(redirectResult.accessToken);
          return;
        }
      }
    } catch (e) {
      // A thrown error here means AAD redirected back with an error (e.g. the 400
      // from the token endpoint). Mark as returned so we don't loop, then let the
      // error propagate so the app shows it rather than silently re-redirecting.
      justReturnedFromRedirect = true;
      throw e;
    }

    // On Azure SWA verify via EasyAuth; locally /.auth/me returns HTML so skip it.
    let loginHint: string | undefined;
    try {
      const authResponse = await fetch('/.auth/me');
      const contentType = authResponse.headers.get('content-type') ?? '';
      if (authResponse.ok && contentType.includes('application/json')) {
        const authData = await authResponse.json();
        const principal: SwaClientPrincipal | null = authData.clientPrincipal;
        if (!principal) {
          window.location.href =
            '/.auth/login/aad?post_login_redirect_uri=' +
            encodeURIComponent(window.location.href);
          return;
        }
        loginHint = principal.userDetails;
      }
    } catch {
      // /.auth/me unreachable — local dev without SWA emulator
    }

    // Try to acquire the API token silently from cache / refresh token.
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        const result = await this.msalInstance.acquireTokenSilent({ scopes, account: accounts[0] });
        this.setToken(result.accessToken);
        return;
      } catch (e) {
        if (!(e instanceof InteractionRequiredAuthError)) throw e;

        // If we already came back from a redirect and silent acquisition still
        // fails, there is a configuration problem (missing consent, wrong scope,
        // etc.). Throw a clear error instead of starting the loop again.
        if (justReturnedFromRedirect) {
          throw new Error(
            `Unable to acquire API token for scope '${scopes[0]}' after redirect. ` +
            `Ensure the 'user_impersonation' scope on app registration ${environment.aadClientId} ` +
            `has admin consent granted in Azure Portal → App registrations → API permissions.`
          );
        }
      }
    }

    if (justReturnedFromRedirect) {
      throw new Error(
        `Redirect completed but no account found and no token returned. ` +
        `Check Azure AD app registration ${environment.aadClientId} configuration.`
      );
    }

    await this.msalInstance.acquireTokenRedirect({
      scopes,
      ...(loginHint ? { loginHint } : {})
    });
  }

  setToken(token: string): void {
    this.token = token;
  }

  getToken(): string {
    return this.token;
  }

  clearToken(): void {
    this.token = '';
  }

  hasToken(): boolean {
    return this.token.length > 0;
  }
}
